
import { supabase } from "@/integrations/supabase/client";

export const DEFAULT_KIE_API_ENDPOINT = "https://api.kie.ai";
export const KIE_API_ENDPOINT = DEFAULT_KIE_API_ENDPOINT;

export const getKieApiEndpoint = async (): Promise<string> => {
    // Security: Use RPC function to bypass RLS securely
    try {
        const { data, error } = await (supabase.rpc as any)("get_kie_api_endpoint");
        if (!error && data) return data as string;
    } catch (e) {
        console.warn("Failed to fetch KIE_API_ENDPOINT:", e);
    }

    return import.meta.env.VITE_KIE_API_ENDPOINT || DEFAULT_KIE_API_ENDPOINT;
};

export const getKieCallbackUrl = async (): Promise<string> => {
    // Security: Use RPC function to bypass RLS securely
    try {
        const { data, error } = await (supabase.rpc as any)("get_kie_callback_url");
        if (!error && data) return data as string;
    } catch (e) {
        console.warn("Failed to fetch KIE_CALLBACK_URL:", e);
    }

    if (import.meta.env.VITE_KIE_CALLBACK_URL) {
        return import.meta.env.VITE_KIE_CALLBACK_URL;
    }

    // Default to Supabase function endpoint if we have the URL
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    if (supabaseUrl) {
        return `${supabaseUrl}/functions/v1/kie-callback`;
    }

    return "";
};

export async function submitKieTask(endpoint: string, apiKey: string, body: any) {
    const baseUrl = await getKieApiEndpoint();

    // Safeguard: Ensure body.input is an object, not a string (prevents double-stringify bug)
    if (body.input && typeof body.input === 'string') {
        try {
            body.input = JSON.parse(body.input);
            console.warn('⚠️ submitKieTask: Fixed double-stringified input');
        } catch (e) {
            console.warn('⚠️ submitKieTask: body.input is a non-JSON string, keeping as-is');
        }
    }

    const response = await fetch(`${baseUrl}${endpoint}`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${apiKey}`,
        },
        body: JSON.stringify(body),
    });

    if (!response.ok) {
        let errorMessage = `API Error: ${response.status} ${response.statusText}`;
        try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorData.error || errorMessage;
        } catch (e) {
            // Fallback if not JSON
            const text = await response.text().catch(() => "");
            if (text) errorMessage += ` - ${text.substring(0, 100)}`;
        }
        throw new Error(errorMessage);
    }

    return await response.json();
}

export async function pollKieTask(taskId: string, apiKey: string, type: string = "modify") {
    // Common endpoints for polling result:
    // For most multimedia tasks: /api/v1/modify/{taskId}
    // For Suno (V6): /api/v1/jobs/recordInfo?taskId={taskId}
    // For Market models (Seedream, etc): /api/v1/jobs/recordInfo?taskId={taskId}
    let endpoint = "";
    if (type === "suno") endpoint = `/api/v1/jobs/recordInfo?taskId=${taskId}`;
    else if (type === "market") endpoint = `/api/v1/jobs/recordInfo?taskId=${taskId}`;
    else if (type === "veo") endpoint = `/api/v1/veo/record-info?taskId=${taskId}`;
    else endpoint = `/api/v1/modify/${taskId}`;

    const baseUrl = await getKieApiEndpoint();
    const response = await fetch(`${baseUrl}${endpoint}`, {
        method: "GET",
        headers: {
            "Authorization": `Bearer ${apiKey}`,
        },
    });

    if (!response.ok) {
        throw new Error(`Failed to poll task: ${response.statusText}`);
    }

    return await response.json();
}

export const getKieApiKey = async (): Promise<string> => {
    // Security: Use RPC function to bypass RLS securely
    try {
        const { data, error } = await (supabase.rpc as any)("get_kie_api_key");
        if (!error && data) return data as string;
    } catch (e) {
        console.warn("Failed to fetch KIE_API_KEY:", e);
    }

    // Fallback to environment variable only
    const envKey = import.meta.env.VITE_KIE_API_KEY;
    if (envKey) return envKey;

    return "";
};

export const getGeminiApiKey = async (): Promise<string> => {
    // Security: Use RPC function to bypass RLS securely
    try {
        const { data, error } = await (supabase.rpc as any)("get_gemini_api_key");
        if (!error && data) return data as string;
    } catch (e) {
        console.warn("Failed to fetch GEMINI_API_KEY:", e);
    }

    // Fallback to environment variable only
    return import.meta.env.VITE_GEMINI_API_KEY || "";
};

// Clear settings cache (for future use when database settings are implemented)
export const clearSettingsCache = () => {
    // Currently just a placeholder for when database settings are implemented
    console.log("Settings cache cleared");
};

/**
 * Upload a file to Kie.ai temporary storage via Supabase Edge Function proxy
 * Files are automatically deleted after 3 days
 * @param fileUrl - The URL of the file to upload (from Supabase storage)
 * @param uploadPath - Optional path for organizing files (default: "character-videos")
 * @returns Promise with the download URL from Kie.ai
 */
export async function uploadFileToKie(
    fileUrl: string,
    uploadPath: string = "character-videos"
): Promise<{ success: boolean; downloadUrl?: string; error?: string }> {
    try {
        console.log('📤 Uploading file via proxy to Kie.ai...', { fileUrl, uploadPath });

        // Extract file extension from URL (handle query params like ?token=...)
        const urlPath = fileUrl.split('?')[0];
        const urlExtension = urlPath.split('.').pop()?.toLowerCase();

        // Map common extensions, default to mp4 for videos
        const imageExtensions = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'heic', 'heif'];
        const videoExtensions = ['mp4', 'mov', 'webm', 'avi', 'm4v', 'mkv'];

        let extension = 'mp4'; // default
        if (urlExtension && imageExtensions.includes(urlExtension)) {
            extension = urlExtension === 'jpeg' ? 'jpg' : urlExtension;
        } else if (urlExtension && videoExtensions.includes(urlExtension)) {
            extension = urlExtension;
        } else if (uploadPath.includes('image')) {
            // Fallback: if uploadPath mentions "image", assume it's an image
            extension = 'png';
        }

        const fileName = `${Date.now()}-upload.${extension}`;
        console.log('📦 Upload filename:', fileName, 'from URL extension:', urlExtension);

        const response = await supabase.functions.invoke('kie-api-proxy', {
            body: {
                action: 'uploadFile',
                fileUrl,
                uploadPath,
                fileName
            }
        });

        if (response.error) {
            console.error('❌ Proxy upload error:', response.error);
            return {
                success: false,
                error: response.error.message || 'Proxy upload failed'
            };
        }

        const result = response.data;
        console.log('📦 Proxy Upload Response:', result);

        if (!result.success || !result.downloadUrl) {
            return {
                success: false,
                error: result.error || 'No download URL returned from upload'
            };
        }

        console.log('✅ File uploaded to Kie.ai via proxy:', result.downloadUrl);
        return {
            success: true,
            downloadUrl: result.downloadUrl
        };
    } catch (error: any) {
        console.error('❌ Kie.ai proxy upload error:', error);
        return {
            success: false,
            error: error.message || 'Failed to upload file'
        };
    }
}

/**
 * Submit a task to Kie.ai via Supabase Edge Function proxy
 * Use this for API calls that would otherwise be blocked by CORS
 */
export async function submitKieTaskViaProxy(
    endpoint: string,
    body: any
): Promise<any> {
    try {
        console.log('🚀 Submitting task via proxy...', { endpoint });

        // Safeguard: Ensure body.input is an object, not a string
        if (body.input && typeof body.input === 'string') {
            try {
                body.input = JSON.parse(body.input);
                console.warn('⚠️ submitKieTaskViaProxy: Fixed double-stringified input');
            } catch (e) {
                console.warn('⚠️ submitKieTaskViaProxy: body.input is a non-JSON string, keeping as-is');
            }
        }

        const response = await supabase.functions.invoke('kie-api-proxy', {
            body: {
                action: 'createTask',
                endpoint,
                body
            }
        });

        if (response.error) {
            throw new Error(response.error.message || 'Proxy request failed');
        }

        console.log('📦 Proxy Response:', response.data);
        return response.data;
    } catch (error: any) {
        console.error('❌ Proxy submit error:', error);
        throw error;
    }
}

/**
 * Poll a task from Kie.ai via Supabase Edge Function proxy
 */
export async function pollKieTaskViaProxy(
    taskId: string,
    pollType: string = "market"
): Promise<any> {
    try {
        const response = await supabase.functions.invoke('kie-api-proxy', {
            body: {
                action: 'pollTask',
                taskId,
                pollType
            }
        });

        if (response.error) {
            throw new Error(response.error.message || 'Proxy poll failed');
        }

        return response.data;
    } catch (error: any) {
        console.error('❌ Proxy poll error:', error);
        throw error;
    }
}
